import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

import { MonthYear } from '../../../utils/types';
import { Modal } from '../../../../core/services/modal';
import { Button } from "../../../ui/button/button";

@Component({
  selector: 'app-date-picker-modal',
  imports: [CommonModule, TranslatePipe, Button],
  templateUrl: './date-picker-modal.html',
  styleUrl: './date-picker-modal.css'
})
export class DatePickerModal {
  @Input() options!: any;

  selectedDate: Date | null = null;

  // Selection
  day = '';
  month = '';
  year: number | null = null;
  isDaySelected = false;
  isMonthSelected = false;
  isYearSelected = false;

  // View
  currentSelectionTab: 'year' | 'month' | 'day' | 'confirm' = 'year'; //confirm to show content when date is selected and confirmed
  currentMonthYear: MonthYear = {
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  };

  // Calendar
  daysInMonth: number[] = [];
  firstDayOfMonth = 0;

  // Today
  today = new Date();
  currentYear = this.today.getFullYear();

  // Navigation
  canGoPrev = false;   // Right arrow → go back
  canGoNext = true;    // Left arrow → go forward

  // Year Pagination
  private readonly YEARS_PER_PAGE = 12;
  private yearPageIndex = 0;
  displayedYearRange: number[] = [];
  canPrevYearPage = false;
  canNextYearPage = false;
  private allYears: number[] = [];

  constructor(private modalService: Modal) { }

  ngOnInit() {
    this.generateFullYearRange();

    console.log(this.options, ",")

    if (this.options.defaultSelection === 'month') {
      this.currentSelectionTab = 'month';
      this.month = this.options?.prefilledDate;
    } else if (this.options.defaultSelection === 'year') {
      this.currentSelectionTab = 'year'
    } else {
      // Pick target year — from selectedDate if available, otherwise today
      const targetYear = this.options?.prefilledDate
        ? this.options?.prefilledDate.getFullYear()
        : this.currentYear;

      // Set pagination to show the page that includes target year
      const index = this.allYears.indexOf(targetYear);
      if (index !== -1) {
        this.yearPageIndex = Math.floor(index / this.YEARS_PER_PAGE);
      }
    }

    this.updateYearPage();
    this.updateCalendar();
    this.updateNavigationState();

    /** If there’s a prefilled date, sync it */
    if (this.options?.prefilledDate) {
      const prefilled = new Date(this.options.prefilledDate);
      if (this.isValidDate(prefilled)) {
        this.setDate(prefilled);
        this.syncFromDate(prefilled);
      }
    }
  }

  /* ── YEAR RANGE ── */
  private generateFullYearRange() {
    const min = this.options.minYear ?? 1900;
    const max = this.options.maxYear ?? 2100;
    this.allYears = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  }

  private updateYearPage() {
    const start = this.yearPageIndex * this.YEARS_PER_PAGE;
    const end = start + this.YEARS_PER_PAGE;
    this.displayedYearRange = this.allYears.slice(start, end);
    this.canPrevYearPage = this.yearPageIndex > 0;
    this.canNextYearPage = end < this.allYears.length;
  }

  prevYearPage() { if (this.canPrevYearPage) { this.yearPageIndex--; this.updateYearPage(); } }
  nextYearPage() { if (this.canNextYearPage) { this.yearPageIndex++; this.updateYearPage(); } }

  /* ── SELECTION ── */
  selectYear(y: number) {
    this.year = y;
    this.isYearSelected = true;
    this.currentMonthYear.year = y;

    if (this.options.defaultSelection === 'year') {
      this.completeSelection();
      return;
    }
    this.currentSelectionTab = 'month';
    this.updateCalendar();
    this.updateNavigationState();
  }

  selectMonth(m: number) {
    this.month = new Date(0, m).toLocaleString('default', { month: 'short' });
    this.isMonthSelected = true;
    this.currentMonthYear.month = m;

    if (this.options.defaultSelection === 'month') {
      this.completeSelection();
      this.close();
      return;
    }
    this.currentSelectionTab = 'day';
    this.updateCalendar();
    this.updateNavigationState();
  }

  selectDay(d: number) {
    this.day = String(d).padStart(2, '0');
    this.isDaySelected = true;
    this.completeSelection();
  }

  private completeSelection() {
    let selected: Date | null = null;
    if (this.options.defaultSelection === 'year' && this.year) {
      this.options.onConfirm(this.year);
    } else if (this.options.defaultSelection === 'month' && this.isMonthSelected) {
      const m = this.getMonthIndex(this.month);
      this.options.onConfirm(this.getMonthShortName(m));
    } else if (this.year && this.month && this.day) {
      const m = this.getMonthIndex(this.month);
      selected = new Date(this.year, m, parseInt(this.day, 10));
    }

    if (selected && this.isValidDate(selected)) {
      this.setDate(selected); //here the date is selected
    }
  }

  /* ── NAVIGATION (Arrows) ── */
  goNextTab() {
    if (!this.canGoNext) return;
    if (this.currentSelectionTab === 'year') {
      this.currentSelectionTab = 'month';
    } else if (this.currentSelectionTab === 'month') {
      this.currentSelectionTab = 'day';
    }
    this.updateNavigationState();
  }

  goPrevTab() {
    if (!this.canGoPrev) return;
    if (this.currentSelectionTab === 'day') {
      this.currentSelectionTab = 'month';
    } else if (this.currentSelectionTab === 'month') {
      this.currentSelectionTab = 'year';
    }
    this.updateNavigationState();
  }

  private updateNavigationState() {
    // Left arrow → forward
    this.canGoNext = this.currentSelectionTab === 'year' ? this.isYearSelected : this.currentSelectionTab === 'month' ? this.isMonthSelected : this.options.defaultSelection === 'all';

    // Right arrow → backward
    this.canGoPrev = this.currentSelectionTab !== 'year';
  }

  /* ── CALENDAR ── */
  updateCalendar() {
    const { year, month } = this.currentMonthYear;
    const date = new Date(year, month, 1);
    this.firstDayOfMonth = date.getDay();
    const lastDay = new Date(year, month + 1, 0).getDate();
    this.daysInMonth = Array.from({ length: lastDay }, (_, i) => i + 1);
  }

  isValidDate(d: Date): boolean { return d instanceof Date && !isNaN(d.getTime()); }

  private syncFromDate(d: Date) {
    this.year = d.getFullYear();
    this.month = d.toLocaleString('default', { month: 'short' });
    this.day = String(d.getDate()).padStart(2, '0');
    this.isYearSelected = this.isMonthSelected = this.isDaySelected = true;
    this.currentMonthYear = { month: d.getMonth(), year: d.getFullYear() };
    this.currentSelectionTab = 'day';
    this.updateCalendar();
  }

  setDate(d: Date | null) {
    this.selectedDate = d;
  }

  getMonthShortName(i: number): string { return new Date(0, i).toLocaleString('default', { month: 'short' }); }

  getMonthIndex(abbr: string): number {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.findIndex(m => m.toLowerCase() === abbr?.toLowerCase());
  }

  moveToConfrimTab() {
    if (!this.selectedDate) return;
    this.currentSelectionTab = 'confirm';
  }

  confirmDate() {
    if (!this.selectedDate) return;
    this.options.onConfirm(this.selectedDate);
    this.close();
  }

  close(): void {
    this.modalService.close();
  }

  goBack() {
    this.currentSelectionTab = this.options.defaultSelection === 'all' ? 'year' : this.options.defaultSelection;
  }
}
